import { localCatalog } from '../data/catalog'
import { localSite } from '../data/site'
import { policy } from '../data/policies'
import { socialPosts } from '../admin/social/socialSeed'
import { summarize } from '../admin/customers/customerRules'
import { ApiError } from '../core/api/http'
import { dayLoad } from '../core/rules/agenda'
import { salesReport, previousRange } from '../core/rules/revenue'
import { isLowStock } from '../core/rules/stock'
import { COHORT_MONTHS } from '../core/rules/insights/stats'
import { signals } from '../core/rules/insights/signals'
import { normalizeSearchTerm } from '../core/rules/insights/searchTerms'
import { bestSlot } from '../core/rules/insights/social'
import { dayMedium, fromIso, iso, monthEnd, monthStart, nowStamp, shiftDate, shiftMonths, startOfWeek, today } from '../core/format/dates'
import { db } from './db'
import { reply } from './reply'
import { allRevenue } from './salesBackend'
import { demoAdminIdentity } from './adminIdentity'
import {
  captions,
  daysOf,
  deviceShares,
  isPostDay,
  newsletterSubjects,
  noise,
  ownLinks,
  pageShares,
  querySeed,
  scaled,
  searchPageSeed,
  searchSeed,
  slippingGuests,
  sourceShares,
  stopReasons,
  visitsOn,
} from './seed/insights'
import type { AdminBackend } from '../core/types/backend'
import type { Comparable, IntegrationId, Integrations, SocialPostStats, VisitorsReport } from '../core/types/insights'
import type { MailSeriesKey } from '../core/types/mail'

/**
 * DEMO — Inzicht. Omzet, afspraken, voorraad, mislukte incasso's en wegglijdende klanten komen
 * uit de demo-tabellen; de rest zijn vaste voorbeeldcijfers (demo/seed/insights.ts). De signalen
 * gaan door dezelfde regels als op de server (core/rules/insights/signals.ts).
 */

type Methods = Pick<
  AdminBackend,
  | 'fetchInsightsOverview'
  | 'fetchInsightsCustomers'
  | 'fetchInsightsSalon'
  | 'fetchInsightsWebshop'
  | 'fetchInsightsSources'
  | 'fetchInsightsVisitors'
  | 'fetchInsightsSearch'
  | 'fetchInsightsBusiness'
  | 'fetchInsightsSocial'
  | 'fetchInsightsMail'
  | 'fetchIntegrations'
  | 'refreshIntegration'
>

/** Het Bedrijfsprofiel staat bewust niet gekoppeld: zo is de uitleg ook te zien. Per sessie. */
const integrations: Integrations = (() => {
  const at = `${today()}T04:12`
  return {
    umami: { status: 'gekoppeld', updatedAt: at },
    searchConsole: { status: 'gekoppeld', updatedAt: `${shiftDate(today(), -3)}T04:15`, message: 'Search Console loopt 3 dagen achter.' },
    googleBusiness: { status: 'niet-gekoppeld' },
    instagram: { status: 'gekoppeld', updatedAt: at },
    brevo: { status: 'gekoppeld', updatedAt: at },
  }
})()

const connected = (id: IntegrationId) => integrations[id].status !== 'niet-gekoppeld'

const both = (fn: (from: string, to: string) => number | null, from: string, to: string): Comparable => {
  const prev = previousRange(from, to)
  return { current: fn(from, to), previous: fn(prev.from, prev.to) }
}

const inRange = (date: string, from: string, to: string) => date >= from && date <= to

// ——— Uit de demo-tabellen ———

const revenueOf = (channel: 'webshop' | 'salon') => (from: string, to: string) =>
  salesReport(allRevenue(), from, to, 'day').byChannel.find((c) => c.channel === channel)?.revenueExVatCents ?? 0

const webshopSales = (from: string, to: string) => salesReport(allRevenue(), from, to, 'day').byChannel.find((c) => c.channel === 'webshop')?.sales ?? 0

const visits = (from: string, to: string) => daysOf(from, to).reduce((s, d) => s + visitsOn(d), 0)

const appointmentsIn = (from: string, to: string) => db.appointments().filter((a) => inRange(a.date, from, to))

const noShowCounts = (from: string, to: string) => {
  const rows = appointmentsIn(from, to).filter((a) => a.outcome !== 'geannuleerd')
  return { total: rows.length, noShow: rows.filter((a) => a.outcome === 'no-show').length }
}

const failedPayments = () => db.accounts().filter((a) => a.subscription?.paymentIssue).length

function slipping() {
  const weeks = policy.revisitAfterWeeks
  const real = db
    .accounts()
    .map(summarize)
    .filter((c) => c.attention.includes('terugzien'))
    .map((c) => ({
      id: c.id,
      name: [c.firstName, c.lastName].filter(Boolean).join(' '),
      lastSeen: c.lastVisit ?? c.lastOrder ?? today(),
      shape: c.shape.home && c.shape.salon ? ('beide' as const) : c.shape.salon ? ('salon' as const) : ('webshop' as const),
    }))
  const guests = slippingGuests.slice(0, Math.max(0, 12 - real.length)).map((name, i) => ({
    id: null,
    name,
    lastSeen: shiftDate(today(), -(weeks * 7 + 4 + i * 9)),
    shape: i % 3 === 0 ? ('webshop' as const) : ('salon' as const),
  }))
  return { weeks, customers: [...real, ...guests].sort((a, b) => a.lastSeen.localeCompare(b.lastSeen)) }
}

function lowStock() {
  const overrides = db.stockOverrides()
  return localCatalog.products
    .filter((p) => p.kind !== 'giftcard')
    .map((p) => (p.id in overrides ? { ...p, stock: overrides[p.id] ?? undefined } : p))
    .filter(isLowStock)
    .map((p) => ({ productId: p.id, name: p.name, stock: p.stock ?? 0, sold30: 4 + Math.round(noise(p.id) * 8) }))
}

// ——— Voorbeeldcijfers ———

function visitorsReport(from: string, to: string): VisitorsReport {
  const days = daysOf(from, to)
  const weekly = days.length > 62
  const buckets = new Map<string, number>()
  days.forEach((d) => {
    const key = weekly ? iso(startOfWeek(fromIso(d))) : d
    buckets.set(key, (buckets.get(key) ?? 0) + visitsOn(d))
  })
  const total = visits(from, to)
  const part = (share: number, key: string) => Math.round(total * share * (0.9 + noise(`${key}${from}`) * 0.2))
  return {
    series: [...buckets].map(([date, v]) => ({ date, visits: v })),
    visits: both(visits, from, to),
    pageviews: both((f, t) => Math.round(visits(f, t) * 2.7), from, to),
    pages: pageShares.map((p) => ({ path: p.path, visits: part(p.share, p.path) })).sort((a, b) => b.visits - a.visits),
    sources: sourceShares.map((s) => ({ source: s.source, label: s.label, visits: part(s.share, s.source) })),
    devices: deviceShares.map((d) => ({ device: d.device, visits: part(d.share, d.device) })),
    searches: searchSeed
      .map((s) => ({ term: normalizeSearchTerm(s.raw), count: Math.round((s.per30 * days.length) / 30), results: s.results }))
      .filter((s): s is { term: string; count: number; results: number } => s.term !== null && s.count > 0)
      .sort((a, b) => b.count - a.count),
  }
}

function searchReport(from: string, to: string) {
  const days = daysOf(from, to).length
  const f = (per30: number, key: string) => Math.round(((per30 * days) / 30) * (0.9 + noise(`${key}${from}`) * 0.2))
  const impressions = (a: string, b: string) => querySeed.reduce((s, q) => s + f(q.per30, `${q.query}${a}${b}`), 0)
  const clicks = (a: string, b: string) => querySeed.reduce((s, q) => s + Math.round(f(q.per30, `${q.query}${a}${b}`) * q.ctr), 0)
  return {
    lagDays: 3,
    clicks: both(clicks, from, to),
    impressions: both(impressions, from, to),
    queries: querySeed.map((q) => {
      const shown = f(q.per30, q.query)
      return { query: q.query, impressions: shown, clicks: Math.round(shown * q.ctr), position: q.position, previousPosition: Math.round((q.position + q.trend) * 10) / 10 }
    }),
    pages: searchPageSeed.map((p) => {
      const shown = f(p.per30, p.path)
      return { path: p.path, title: p.title, impressions: shown, clicks: Math.round(shown * p.ctr), editTo: p.editTo }
    }),
  }
}

function socialPostsBetween(from: string, to: string): SocialPostStats[] {
  const planned = socialPosts.filter((p) => p.status === 'geplaatst')
  const all = daysOf(from, to).filter(isPostDay).reverse()
  return all.map((date, i) => {
    const plannerPost = planned[i]
    const reach = Math.round(380 + noise(`reach${date}`) * 900)
    const name = (id: string) => localCatalog.products.find((p) => p.id === id)?.name ?? id
    return {
      id: `ig-${date}`,
      date,
      time: ['09:30', '12:00', '19:30'][Math.floor(noise(`t${date}`) * 3)],
      caption: plannerPost?.title || captions[Math.floor(noise(`c${date}`) * captions.length)],
      reach,
      likes: Math.round(reach * (0.06 + noise(`l${date}`) * 0.05)),
      saves: Math.round(reach * (0.01 + noise(`s${date}`) * 0.03)),
      shares: Math.round(reach * noise(`sh${date}`) * 0.015),
      profileVisits: Math.round(reach * (0.02 + noise(`pv${date}`) * 0.03)),
      linkClicks: Math.round(reach * noise(`lc${date}`) * 0.02),
      plannerPostId: plannerPost?.id ?? null,
      products: (plannerPost?.productIds ?? []).slice(0, 3).map((id) => ({
        productId: id,
        name: name(id),
        unitsAfter: 2 + Math.round(noise(`ua${id}${date}`) * 6),
        unitsBefore: 1 + Math.round(noise(`ub${id}${date}`) * 4),
      })),
    }
  })
}

const seriesKeys: MailSeriesKey[] = ['welkom', 'verlaten-mand', 'bijna-op', 'review', 'lang-niet-gezien', 'verjaardag']

// ——— De methodes ———

export const demoInsightsBackend: Methods = {
  fetchInsightsOverview(from, to) {
    const seesRevenue = demoAdminIdentity.access().grants['revenue.view'] === 'all'
    const prev = previousRange(from, to)
    const visitorDays = visitorsReport(from, to)
    // Signal "post bracht bezoek" vergelijkt met het gewone dagbezoek in deze periode.
    const posts = socialPostsBetween(from, to)
    const average = visits(from, to) / Math.max(1, daysOf(from, to).length)
    const per100 = (f: string, t: string) => {
      const v = visits(f, t)
      return v > 0 ? Math.round((webshopSales(f, t) / v) * 1000) / 10 : null
    }
    const search = searchReport(from, to)
    return reply(
      {
        kpis: {
          // Zonder `revenue.view` (bijv. Marketing) laat de server de omzet weg.
          revenueWebshopCents: seesRevenue ? both(revenueOf('webshop'), from, to) : { current: null, previous: null },
          revenueSalonCents: seesRevenue ? both(revenueOf('salon'), from, to) : { current: null, previous: null },
          newCustomers: both((f, t) => scaled(f, t, 0.5, 'new'), from, to),
          returningCustomers: both((f, t) => scaled(f, t, 1.4, 'ret'), from, to),
          visits: connected('umami') ? both(visits, from, to) : { current: null, previous: null },
          ordersPer100Visits: connected('umami') ? both(per100, from, to) : { current: null, previous: null },
          bookings: both((f, t) => appointmentsIn(f, t).length, from, to),
          newsletterNet: both((f, t) => scaled(f, t, 0.35, 'nb'), from, to),
        },
        signals: signals({
          zeroResultSearches: visitorDays.searches.filter((s) => s.results === 0),
          slippingCustomers: slipping().customers.length,
          revisitAfterWeeks: policy.revisitAfterWeeks,
          lowStock: lowStock(),
          lowCtrPages: search.pages,
          // De dag na vandaag is er nog niet: die post telt nog niet mee.
          postSpikes: posts.filter((p) => p.date < to).map((p) => ({ postId: p.id, date: dayMedium(p.date), visits: visitsOn(shiftDate(p.date, 1)), average })),
          failedPayments: failedPayments(),
          noShow: { current: noShowCounts(from, to), previous: noShowCounts(prev.from, prev.to) },
        }),
      },
      450,
    )
  },

  fetchInsightsCustomers(from, to) {
    const months = Array.from({ length: 6 }, (_, i) => shiftMonths(monthStart(to), i - 5)).map((month) => ({
      month,
      new: 10 + Math.round(noise(`n${month}`) * 12),
      returning: 26 + Math.round(noise(`r${month}`) * 18),
    }))
    const rates: Record<number, number> = { 1: 0.36, 3: 0.52, 6: 0.61 }
    const cohorts = Array.from({ length: 8 }, (_, i) => shiftMonths(monthStart(to), i - 8)).map((month) => {
      const size = 4 + Math.round(noise(`size${month}`) * 16)
      return {
        month,
        size,
        returned: COHORT_MONTHS.map((k) => (monthEnd(shiftMonths(month, k)) > today() ? null : Math.min(size, Math.round(size * rates[k] * (0.8 + noise(`${month}${k}`) * 0.4))))),
      }
    })
    return reply(
      {
        months,
        averageSpendCents: both((f, t) => 6200 + Math.round(noise(`avg${f}${t}`) * 1400), from, to),
        value12mCents: 31_850,
        value12mCustomers: 46,
        medianDaysToSecond: 38,
        secondPurchaseCustomers: 61,
        slipping: slipping(),
        cohorts,
      },
      400,
    )
  },

  fetchInsightsSalon(from, to) {
    const hours = localSite().openingHours
    const days = daysOf(from, to).map((d) => ({ date: d, ...dayLoad(hours, d, [], []) })).filter((d) => d.open)
    const booked = (d: { date: string; available: number }) => Math.round(d.available * (0.52 + noise(`occ${d.date}`) * 0.4))
    type Load = { bookedMinutes: number; openMinutes: number }
    const weeks = new Map<string, Load>()
    const weekdays = new Map<number, Load>()
    const add = <K,>(m: Map<K, Load>, k: K, d: { date: string; available: number }) => {
      const cur = m.get(k) ?? { bookedMinutes: 0, openMinutes: 0 }
      m.set(k, { bookedMinutes: cur.bookedMinutes + booked(d), openMinutes: cur.openMinutes + d.available })
    }
    days.forEach((d) => {
      add(weeks, iso(startOfWeek(fromIso(d.date))), d)
      // 1 = maandag … 7 = zondag
      add(weekdays, fromIso(d.date).getDay() || 7, d)
    })
    const total = days.length * 6
    const treatments = localCatalog.treatments.slice(0, 6).map((t, i) => {
      const count = Math.max(1, Math.round((total * (0.3 - i * 0.04)) * (0.85 + noise(t.slug) * 0.3)))
      const price = t.price ?? (t.kuur ? t.kuur.price / t.kuur.sessions : 0)
      return { slug: t.slug, name: t.name, count, revenueCents: Math.round((count * price * 100) / 1.21) }
    })
    return reply(
      {
        byWeek: [...weeks].map(([week, v]) => ({ week, ...v })),
        byWeekday: [...weekdays].sort((a, b) => a[0] - b[0]).map(([weekday, v]) => ({ weekday, ...v })),
        appointments: { total, noShow: Math.round(total * 0.03), cancelled: Math.round(total * 0.07) },
        treatments: treatments.sort((a, b) => b.count - a.count),
        revenuePerHourCents: both((f, t) => 8800 + Math.round(noise(`rph${f}${t}`) * 1600), from, to),
        kuren: { sold: scaled(from, to, 0.12, 'kuur'), ended: scaled(from, to, 0.1, 'kend'), completed: scaled(from, to, 0.08, 'kdone'), prepaidOpenCents: 184_500 },
      },
      400,
    )
  },

  fetchInsightsWebshop(from, to) {
    const products = localCatalog.products.filter((p) => p.kind !== 'giftcard').slice(0, 8)
    const v = visits(from, to)
    const views = Math.round(v * 0.48)
    const cart = Math.round(views * 0.11)
    const checkout = Math.round(cart * 0.52)
    const cancelled = scaled(from, to, 0.04, 'stop')
    return reply(
      {
        products: products
          .map((p, i) => {
            const buyers = Math.max(1, scaled(from, to, 0.5 - i * 0.05, p.id))
            return { productId: p.id, name: p.name, revenueCents: Math.round((buyers * 1.3 * p.price * 100) / 1.21), buyers, repeatBuyers: Math.round(buyers * (0.15 + noise(`rep${p.id}`) * 0.3)) }
          })
          .sort((a, b) => b.revenueCents - a.revenueCents),
        pairs: [
          { a: products[0]?.name ?? '', b: products[2]?.name ?? '', count: 9 },
          { a: products[1]?.name ?? '', b: products[3]?.name ?? '', count: 6 },
          { a: products[0]?.name ?? '', b: products[4]?.name ?? '', count: 4 },
        ].filter((p) => p.a && p.b),
        advice: { revenueCents: both((f, t) => scaled(f, t, 2600, 'adv'), from, to), orders: scaled(from, to, 0.3, 'advo') },
        funnel: connected('umami')
          ? [
              { key: 'product', label: 'Productpagina bekeken', count: views, source: 'umami' as const },
              { key: 'mand', label: 'In de mand', count: cart, source: 'umami' as const },
              { key: 'afrekenen', label: 'Afrekenen gestart', count: checkout, source: 'umami' as const },
              { key: 'betaald', label: 'Betaald', count: webshopSales(from, to), source: 'orders' as const },
            ]
          : null,
        subscriptions: {
          active: 23,
          started: scaled(from, to, 0.08, 'sub'),
          cancelled,
          reasons: stopReasons.map((r) => ({ code: r.code, count: Math.round(cancelled * r.weight) })).filter((r) => r.count > 0),
          failedPayments: failedPayments(),
        },
        vouchers: { issuedCents: scaled(from, to, 1800, 'vi'), redeemedCents: scaled(from, to, 1100, 'vr'), outstandingCents: 64_500 },
      },
      400,
    )
  },

  fetchInsightsSources(from, to) {
    const v = visitorsReport(from, to)
    // De echte antwoorden uit de demo (bedankpagina, boeken, kassa) komen bovenop de voorbeeldcijfers,
    // zodat een antwoord dat je zelf geeft hier meteen meetelt.
    const answered = new Map<string, number>()
    db.accounts().forEach((a) => {
      const s = a.customer.acquisitionSource
      if (s && s.at >= from && s.at <= to) answered.set(s.channel, (answered.get(s.channel) ?? 0) + 1)
    })
    const rows = (['instagram', 'google', 'maps', 'via-iemand', 'nieuwsbrief', 'langsgelopen', 'anders', 'onbekend'] as const).map((channel, i) => {
      const weight = [0.28, 0.22, 0.12, 0.16, 0.05, 0.07, 0.02, 0.08][i]
      const newCustomers = Math.round(scaled(from, to, 0.5, 'nc') * weight) + (answered.get(channel) ?? 0)
      const value = 18_000 + Math.round(noise(`val${channel}`) * 22_000)
      return {
        channel,
        newCustomers,
        revenueCents: newCustomers * Math.round(value * 0.35),
        value6mCents: channel === 'onbekend' ? null : Math.round(value * 0.6),
        value12mCents: channel === 'onbekend' || channel === 'anders' ? null : value,
      }
    })
    return reply(
      {
        rows: rows.sort((a, b) => (a.channel === 'onbekend' ? 1 : b.channel === 'onbekend' ? -1 : b.newCustomers - a.newCustomers)),
        links: ownLinks.map((l) => ({ ...l, visits: connected('umami') ? (v.sources.find((s) => s.source === l.utmSource)?.visits ?? 0) : null })),
      },
      350,
    )
  },

  fetchInsightsVisitors: (from, to) => reply(connected('umami') ? visitorsReport(from, to) : null, 400),
  fetchInsightsSearch: (from, to) => reply(connected('searchConsole') ? searchReport(from, to) : null, 400),
  fetchInsightsBusiness: () => reply(null, 300),

  fetchInsightsSocial(from, to) {
    if (!connected('instagram')) return reply(null, 300)
    const posts = socialPostsBetween(from, to)
    const history = socialPostsBetween(shiftDate(today(), -180), today())
    const weeks = daysOf(from, to).filter((d, i, all) => i % 7 === 0 || i === all.length - 1)
    return reply({ followers: weeks.map((date) => ({ date, count: 1840 + Math.round((fromIso(date).getTime() / 864e5 - 20_300) * 1.8) })), posts, best: bestSlot(history), postCount: history.length }, 400)
  },

  fetchInsightsMail(from, to) {
    if (!connected('brevo')) return reply(null, 300)
    const sent = daysOf(from, to).filter((d) => fromIso(d).getDay() === 2 && noise(`nl${d}`) < 0.5)
    return reply(
      {
        newsletters: sent.reverse().map((d, i) => {
          const recipients = 405 + Math.round(noise(`rc${d}`) * 30)
          const opened = Math.round(recipients * (0.41 + noise(`op${d}`) * 0.14))
          const clicked = Math.round(recipients * (0.05 + noise(`cl${d}`) * 0.05))
          const orders = Math.round(clicked * 0.12)
          return {
            id: `nb-${d}`,
            subject: newsletterSubjects[i % newsletterSubjects.length],
            sentAt: `${d}T09:30`,
            stats: { recipients, opened, clicked, unsubscribed: Math.round(noise(`un${d}`) * 4), bounced: Math.round(noise(`bo${d}`) * 3), orders, revenueCents: orders * 5400 },
          }
        }),
        series: seriesKeys.map((key) => {
          const count = scaled(from, to, { welkom: 0.5, 'verlaten-mand': 0.6, 'bijna-op': 0.4, review: 0.5, 'lang-niet-gezien': 0.2, verjaardag: 0.1 }[key], key)
          return {
            key,
            sent: count,
            opened: Math.round(count * (0.48 + noise(`so${key}`) * 0.2)),
            clicked: Math.round(count * (0.08 + noise(`sc${key}`) * 0.1)),
            recovered: key === 'verlaten-mand' ? Math.round(count * 0.14) : null,
          }
        }),
      },
      400,
    )
  },

  fetchIntegrations: () => reply({ ...integrations }, 250),

  async refreshIntegration(id) {
    if (!connected(id)) {
      await reply(null, 300)
      throw new ApiError('Deze bron is nog niet gekoppeld.', 404, undefined, 'not_connected')
    }
    integrations[id] = { ...integrations[id], status: 'gekoppeld', updatedAt: nowStamp() }
    return reply(integrations[id], 600)
  },
}
